import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import Collapse from 'react-bootstrap/Collapse';
import { DayCustomersStatistics } from '@api/shipping-data/types/DayCustomersStatistics';
import { DeliveryStatus } from '@api/shipping-data/types/DeliveryStatus';
import { setSelectedCustomerUnvId } from '@pages/orders/store/slice';
import { CustomerIcons } from 'components/order-elements/CustomerIcons';
import { InfobarValues, Infobar } from 'components/order-elements/Infobar';
import Detail from '@pages/orders/detail/Detail';



interface CustomerItemProps {
	item: DayCustomersStatistics,
	dateWithoutTime: string,
	isSmallScreen: boolean,
	isSelected: boolean,
}


const CustomerItem = (props: CustomerItemProps) => {
	const { dateWithoutTime, isSmallScreen, isSelected } = props;
	const { orderUnvDestinationId, orderDestination, shipmentDistributionCode,
		totalDeliveredDeliveries, totalDeliveries, totalBeingBeDeliveredDeliveries, totalToBeDeliveredDeliveries,
		totalReducedOrders, totalCancelledOrders, sentSmses,
		ordersContainingBeer, ordersContainingFreeBeer, ordersContainingTank, ordersContainingCollection,
		ordersContainingEmpties, ordersContainingPosm, ordersContainingClaim, ordersContainingUnknown,
		totalOrders, totalOrdersItemsVolume, totalToBeDeliveredDeliveriesVolume, totalDeliveredDeliveriesVolume,
		totalReducedDeliveriesDueToPpas, totalReducedDeliveredDeliveriesDueToPpas } = props.item;

	const customerUnvId = orderUnvDestinationId ? orderUnvDestinationId : '-';
	const customerClientId = orderDestination && orderDestination.clientId ? orderDestination.clientId : '-';
	const companyName = orderDestination && orderDestination.companyName ? orderDestination.companyName : '-';
	const customerClassification = orderDestination && orderDestination.customerClassification ? orderDestination.customerClassification : '-';
	const address = orderDestination && orderDestination.addressess && orderDestination.addressess.length > 0 ? orderDestination.addressess[0] : null;
	const country = orderDestination && orderDestination.country ? orderDestination.country : '';
	const tradeCode = orderDestination && orderDestination.district && orderDestination.district.sdName ? orderDestination.district.sdName : '-';
	const distributionCode = shipmentDistributionCode ? shipmentDistributionCode : '-';
	const deliveryStatus = totalDeliveredDeliveries === totalDeliveries
		? DeliveryStatus.delivered
		: totalBeingBeDeliveredDeliveries > 0
		? DeliveryStatus.truck
		: totalToBeDeliveredDeliveries > 0
		? DeliveryStatus.dc
		: DeliveryStatus.none;
	const isReduced = totalReducedOrders ? totalReducedOrders > 0 : false;
	const isCancelled = totalCancelledOrders ? totalCancelledOrders > 0 : false;
	const isDelivered = totalDeliveredDeliveries > 0;
	const smsWasSend = sentSmses > 0;
	const containsBeer = ordersContainingBeer > 0;
	const containsTank = ordersContainingTank > 0;
	const containsFreeBeer = ordersContainingFreeBeer > 0;
	const containsCollection = ordersContainingCollection > 0;
	const containsEmpties = ordersContainingEmpties > 0;
	const containsPosm = ordersContainingPosm > 0;
	const containsClaim = ordersContainingClaim > 0;
	const containsUnknown = ordersContainingUnknown > 0;
	const countValues: InfobarValues = {
		value1: totalOrders,
		showExclamation1: Boolean(totalReducedOrders),
		value2: totalDeliveries,
		showExclamation2: Boolean(totalReducedDeliveriesDueToPpas),
		value3: totalDeliveredDeliveries,
		showExclamation3: Boolean(totalReducedDeliveredDeliveriesDueToPpas),
	};
	const volumeValues: InfobarValues = {
		value1: totalOrdersItemsVolume,
		showExclamation1: Boolean(totalReducedOrders),
		value2: totalToBeDeliveredDeliveriesVolume,
		showExclamation2: Boolean(totalReducedDeliveriesDueToPpas),
		value3: totalDeliveredDeliveriesVolume,
		showExclamation3: Boolean(totalReducedDeliveredDeliveriesDueToPpas),
	};
	
	const dispatch = useDispatch();
	const runSetSelectedCustomerUnvId = (customerUnvId: string | null) => dispatch(setSelectedCustomerUnvId(customerUnvId));

	const timerRef = useRef<NodeJS.Timeout>(null);
	const [ open, setOpen ] = useState(false);

	
	useEffect(() => {
		if (open && !isSelected) {
			setOpen(false);
		}
	}, [ isSelected ]);

	
	const handleClick = useCallback(() => {
		if (!isSmallScreen) {
			runSetSelectedCustomerUnvId(customerUnvId);
		}
		if (isSmallScreen && !timerRef.current) {
			timerRef.current = setTimeout(() => {
				if (timerRef.current) {
					timerRef.current = null;
				}
			}, 300);
			setOpen(!open);
		}
	},[ timerRef.current, customerUnvId, open, isSmallScreen ]);


	return (
		<div>
			<div className={`customer-item${isSelected ? ' selected-customer' : ''}`}
				id={`customer-client-id:${customerClientId}`}
				onClick={handleClick}>

				<p className="customer-name">{companyName}</p>
				<div className="customer-category">
					<p>{customerClientId}</p>
					<p className="category-icon">{customerClassification}</p>
				</div>
				
				
				<p className="customer-street">{address && address.street ? address.street : '-'}</p>
				<p className="customer-city">{address && address.city ? address.city : '-'}</p>
				<p className="customer-zip">{address ?
					country ? `${country} ${address.zipCode}` : address.zipCode
					: '-'}</p>
				
				
				<p className='customer-dates'></p>
				<p className="customer-direct">{tradeCode}</p>
				<p className="customer-distribution-type">{distributionCode}</p>
				
				
				<CustomerIcons 
					deliveryStatus={deliveryStatus}
					isCancelled={isCancelled}
					isReduced={isReduced}
					smsWasSend={smsWasSend}
				/>
				<Infobar
					containsBeer={containsBeer}
					containsTank={containsTank}
					containsFreeBeer={containsFreeBeer}
					containsCollection={containsCollection}
					containsEmpties={containsEmpties}
					containsPosm={containsPosm}
					containsClaim={containsClaim}
					containsUnknown={containsUnknown}
					isCancelled={isCancelled}
					isReduced={isReduced}
					isDelivered={isDelivered}
					countValues={countValues}
					volumeValues={volumeValues}
				/>

			</div>
			{isSmallScreen &&
				<Collapse in={open}
					onEnter={() => runSetSelectedCustomerUnvId(customerUnvId)}
					onExited={() => runSetSelectedCustomerUnvId(null)}
					>
					<div>
						<Detail
							dateWithoutTime={dateWithoutTime}
							isSmallScreen={isSmallScreen} />
					</div>
				</Collapse>}
		</div>
	)
}

export default CustomerItem;